/**
 * Formatting helpers for the partners table's live quote columns.
 * Pure functions — no I/O, safe on server and client.
 */

const T = 1_000_000_000_000;
const B = 1_000_000_000;
const M = 1_000_000;

/**
 * Format a daily change percentage as a signed two-decimal string.
 * `2.345` → `"+2.35%"`, `-1.2` → `"-1.20%"`.
 */
export function formatChangePct(pct) {
  if (!Number.isFinite(pct)) return null;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
}

/**
 * Format a market cap (in USD) as a compact "$3.20T", "$160B", "$700M" string.
 * Returns null when the input is not a positive finite number.
 */
export function formatMarketCap(cap) {
  if (!Number.isFinite(cap) || cap <= 0) return null;
  if (cap >= T) return `$${trimZeros((cap / T).toFixed(2))}T`;
  if (cap >= B) {
    const billions = cap / B;
    return billions >= 100
      ? `$${Math.round(billions)}B`
      : `$${trimZeros(billions.toFixed(2))}B`;
  }
  if (cap >= M) return `$${Math.round(cap / M)}M`;
  return `$${Math.round(cap)}`;
}

function trimZeros(s) {
  return s.replace(/\.?0+$/, '');
}

/** Numeric market cap expressed in billions (for sortable column). */
export function marketCapBillions(cap) {
  if (!Number.isFinite(cap) || cap <= 0) return -1;
  return cap / B;
}

/**
 * Currencies traditionally quoted without minor units. Yahoo also reports
 * KRW/JPY share prices as whole numbers, so we round.
 */
const ZERO_DECIMAL_CURRENCIES = new Set(['JPY', 'KRW', 'IDR', 'VND', 'CLP', 'HUF']);

/**
 * Format a share price in its trading currency, e.g.
 *   `formatPrice(218.545, 'USD')` → `"$218.55"`,
 *   `formatPrice(197000, 'KRW')` → `"₩197,000"`,
 *   `formatPrice(12.88,   'HKD')` → `"HK$12.88"`.
 * Falls back to a plain numeric string with the ISO suffix if Intl rejects
 * the currency code (e.g. unknown / synthetic codes).
 */
export function formatPrice(value, currency) {
  if (!Number.isFinite(value) || value <= 0) return null;
  const code = (currency || 'USD').toUpperCase();
  const maxDigits = ZERO_DECIMAL_CURRENCIES.has(code) ? 0 : 2;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: maxDigits,
      maximumFractionDigits: maxDigits,
    }).format(value);
  } catch {
    return `${value.toFixed(maxDigits)} ${code}`;
  }
}
